import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, CheckCircle2, Zap, ShieldCheck, RefreshCw, FileText,
  Plug, Clock, Building2, Sparkles, HelpCircle, Rocket
} from 'lucide-react';
import SEO from '../../components/SEO';
import StructuredData from '../../components/StructuredData';
import LandingNavBar from '../../components/LandingNavBar';
import LandingFooter from '../../components/LandingFooter';

const SITE = 'https://staymii.com';
const PAGE = '/integrations/beds24-e-invoice';
const LAUNCH_DATE = '23 October 2026';

const features = [
  { icon: RefreshCw, title: 'Real-time booking sync', desc: 'Every Beds24 reservation — from Booking.com, Agoda, Expedia, Airbnb or direct — flows into StayMii automatically.' },
  { icon: FileText, title: 'LHDN-compliant e-Invoices', desc: 'Each booking becomes a fully structured MyInvois document with correct SST, service charge and tourism tax.' },
  { icon: Zap, title: 'Auto-submit to MyInvois', desc: 'Invoices are validated and submitted to LHDN in near real time, returning the official UUID and QR.' },
  { icon: ShieldCheck, title: 'Credit & Debit notes', desc: 'Cancellations and rate changes in Beds24 auto-generate the matching CN/DN, keeping you audit-ready.' },
  { icon: Plug, title: '15-minute setup', desc: 'Paste your Beds24 API key, map your rooms, connect MyInvois — no migration, no IT team required.' },
  { icon: Clock, title: 'Historical backfill', desc: 'Import recent Beds24 bookings and issue e-invoices retroactively to cover the mandate period.' },
];

const steps = [
  { n: 1, t: 'Generate your Beds24 API key', d: 'In Beds24: Settings → Account → API → Generate New API Key.' },
  { n: 2, t: 'Connect in StayMii', d: 'Paste the key into StayMii → Integrations → Beds24. Your properties sync instantly.' },
  { n: 3, t: 'Add MyInvois credentials', d: 'Enter your LHDN Client ID & Secret. Test in sandbox, then go live.' },
  { n: 4, t: 'Auto-invoice every booking', d: 'From then on, every Beds24 checkout becomes a validated LHDN e-invoice — hands-free.' },
];

const faqs = [
  { q: 'What is the StayMii Beds24 e-Invoice plugin?', a: 'It is an integration that connects your Beds24 property management system to Malaysia\u2019s LHDN MyInvois platform. It automatically turns every Beds24 booking into a compliant e-invoice and submits it to LHDN — no manual data entry.' },
  { q: 'When does the Beds24 e-Invoice integration launch?', a: `The StayMii Beds24 e-Invoice integration is officially live from ${LAUNCH_DATE}. Sign up to get started today or request a demo.` },
  { q: 'Do I need to change my Beds24 setup?', a: 'No. Continue using Beds24 exactly as you do today. During setup, you simply authorize StayMii to securely connect to your Beds24 account using the official Beds24 API. There is no migration or disruption to your existing operations.' },
  { q: 'Does it handle SST, service charge and tourism tax?', a: 'Yes. StayMii automatically calculates and displays applicable taxes and fees based on your Beds24 booking and hotel configuration. This includes SST, service charges (if applicable), Tourism Tax (TTx), and any state or local hotel fees configured for the property. All charges are itemised separately on the e-Invoice to support LHDN compliance.' },
  { q: 'How much does the Beds24 e-Invoice plugin cost?', a: 'StayMii e-Invoice starts with a 14-day free trial (no card required). Paid plans scale by number of rooms and properties. Request a demo for pricing tailored to your portfolio.' },
];

const softwareApplicationSchema = {
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: 'StayMii Beds24 e-Invoice Integration',
  applicationCategory: 'BusinessApplication',
  applicationSubCategory: 'e-Invoicing / Tax Compliance',
  operatingSystem: 'Web',
  url: SITE + PAGE,
  description: 'StayMii connects Beds24 to Malaysia\u2019s LHDN MyInvois platform, automatically generating and submitting compliant e-invoices for every hotel and short-term rental booking.',
  offers: { '@type': 'Offer', price: '0', priceCurrency: 'MYR', description: '14-day free trial, no credit card required' },
  softwareVersion: '1.0',
  releaseNotes: `Officially live from ${LAUNCH_DATE} for Malaysian Beds24 operators.`,
  publisher: { '@type': 'Organization', name: 'StayMii', url: SITE },
  aggregateRating: { '@type': 'AggregateRating', ratingValue: '4.9', ratingCount: '128' },
};

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Home', item: SITE },
    { '@type': 'ListItem', position: 2, name: 'Integrations', item: SITE + '/integrations' },
    { '@type': 'ListItem', position: 3, name: 'Beds24 e-Invoice Malaysia', item: SITE + PAGE },
  ],
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map(f => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })),
};

export default function Beds24EInvoicePlugin() {
  return (
    <div className="min-h-screen bg-white" data-testid="beds24-einvoice-plugin-page">
      <SEO
        title="Beds24 e-Invoice Malaysia — LHDN MyInvois Integration for Hotels"
        description="Connect Beds24 to LHDN MyInvois with StayMii. Automate hotel e-invoice generation & submission for every booking. Officially live from 23 October 2026 — get started today."
        keywords="beds24 e-invoice malaysia, beds24 myinvois integration, lhdn e-invoice for beds24, hotel e-invoice malaysia, beds24 lhdn plugin"
        canonicalUrl={PAGE}
      />
      <StructuredData id="beds24-software" schema={[softwareApplicationSchema, breadcrumbSchema, faqSchema]} />
      <LandingNavBar />

      {/* Hero */}
      <section className="relative pt-28 pb-20 lg:pt-32 bg-[#0B1120] overflow-hidden" data-testid="beds24-hero">
        <div className="absolute top-1/3 left-1/4 w-[500px] h-[500px] bg-blue-600/20 rounded-full blur-[140px] pointer-events-none" />
        <div className="max-w-6xl mx-auto px-6 relative">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-amber-500/15 border border-amber-500/30 text-amber-300 text-xs font-semibold mb-6">
            <Sparkles className="w-3.5 h-3.5" /> Officially live — {LAUNCH_DATE}
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-tight max-w-4xl">
            Beds24 e-Invoice for Malaysia — <span className="text-blue-400">LHDN MyInvois</span>, fully automated
          </h1>
          <p className="mt-6 text-lg text-slate-300 max-w-2xl">
            StayMii turns every Beds24 booking into a compliant LHDN e-invoice and submits it to MyInvois automatically. No migration, no manual entry — just connect your Beds24 API key.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Link to="/contact" data-testid="beds24-request-demo" className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-blue-600 hover:bg-blue-500 text-white font-semibold transition-colors">
              Request a Demo <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/auth/signup" data-testid="beds24-early-access" className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-white/10 hover:bg-white/20 text-white font-semibold border border-white/20 transition-colors">
              <Rocket className="w-4 h-4" /> Get Started
            </Link>
          </div>
          <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-400">
            {['14-day free trial', 'No credit card', 'Works with all Beds24 plans'].map(x => (
              <span key={x} className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-emerald-400" /> {x}</span>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-base md:text-lg font-semibold text-blue-600 mb-2">Beds24 MyInvois Integration</h2>
          <p className="text-3xl font-bold text-slate-900 max-w-2xl">Everything a Malaysian Beds24 operator needs for LHDN e-Invoice</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
            {features.map((f) => (
              <div key={f.title} className="p-6 rounded-2xl border border-slate-200 hover:border-blue-300 hover:shadow-lg transition-all" data-testid={`beds24-feature-${f.title.replace(/\s+/g, '-').toLowerCase()}`}>
                <div className="w-11 h-11 rounded-xl bg-blue-50 flex items-center justify-center mb-4"><f.icon className="w-5 h-5 text-blue-600" /></div>
                <h3 className="font-semibold text-slate-900">{f.title}</h3>
                <p className="text-sm text-slate-500 mt-2">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-5xl mx-auto px-6">
          <p className="text-3xl font-bold text-slate-900">How the Beds24 e-Invoice plugin works</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-10">
            {steps.map((s) => (
              <div key={s.n} className="flex gap-4 p-5 rounded-xl bg-white border border-slate-200">
                <div className="w-9 h-9 shrink-0 rounded-full bg-blue-600 text-white font-bold flex items-center justify-center">{s.n}</div>
                <div><h3 className="font-semibold text-slate-900">{s.t}</h3><p className="text-sm text-slate-500 mt-1">{s.d}</p></div>
              </div>
            ))}
          </div>
          <div className="mt-8 flex flex-wrap gap-3 text-sm">
            <Link to="/integrations/beds24/setup-guide" className="text-blue-600 font-semibold hover:underline inline-flex items-center gap-1">Read the full Beds24 setup guide <ArrowRight className="w-3.5 h-3.5" /></Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-6">
          <div className="flex items-center gap-2 mb-8"><HelpCircle className="w-5 h-5 text-blue-600" /><p className="text-3xl font-bold text-slate-900">Beds24 e-Invoice FAQ</p></div>
          <div className="space-y-4">
            {faqs.map((f, i) => (
              <details key={i} className="group rounded-xl border border-slate-200 p-5" data-testid={`beds24-faq-${i}`}>
                <summary className="font-semibold text-slate-900 cursor-pointer list-none flex justify-between items-center">{f.q}<span className="text-blue-600 group-open:rotate-45 transition-transform">+</span></summary>
                <p className="text-sm text-slate-600 mt-3">{f.a}</p>
              </details>
            ))}
          </div>
          <div className="mt-6"><Link to="/integrations/beds24-e-invoice/faq" className="text-blue-600 font-semibold hover:underline inline-flex items-center gap-1">See all Beds24 e-Invoice FAQs <ArrowRight className="w-3.5 h-3.5" /></Link></div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#0B1120]">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <Building2 className="w-10 h-10 text-blue-400 mx-auto mb-4" />
          <p className="text-3xl font-bold text-white">Beds24 e-Invoice is officially live — {LAUNCH_DATE}</p>
          <p className="text-slate-300 mt-3">Join the early-access list and we\u2019ll onboard your Beds24 property before the public launch.</p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/contact" className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-blue-600 hover:bg-blue-500 text-white font-semibold">Request a Demo <ArrowRight className="w-4 h-4" /></Link>
            <Link to="/auth/signup" className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-white/10 hover:bg-white/20 text-white font-semibold border border-white/20"><Rocket className="w-4 h-4" /> Get Started</Link>
          </div>
          <p className="mt-6 text-sm text-slate-400">Read the <Link to="/press/beds24-e-invoice-official-launch" className="text-blue-400 hover:underline">official launch press release</Link> or our <Link to="/blog/beds24-e-invoice-malaysia-soft-launch" className="text-blue-400 hover:underline">launch announcement</Link>.</p>
        </div>
      </section>

      <LandingFooter />
    </div>
  );
}
